import React, { useState, useEffect } from 'react';
import './NavbarSettings.css';
import { FaPlus, FaTrash, FaArrowUp, FaArrowDown, FaChevronDown, FaEye, FaEyeSlash, FaCaretDown, FaCaretUp, FaImage } from 'react-icons/fa';
import AssetPickerPopup from './AssetPickerPopup';

const NavbarSettings = ({
  element,
  elements,
  setElements,
  updateElementText,
  pages,
  selectedDevice,
  classStylesRegistry,
  setClassStylesRegistry,
  assets
}) => {
  const [menuItems, setMenuItems] = useState([]);
  const [newItemName, setNewItemName] = useState('');
  const [newItemLink, setNewItemLink] = useState('');
  const [showSubmenuFor, setShowSubmenuFor] = useState(null);
  const [newSubmenuItem, setNewSubmenuItem] = useState({ name: '', link: '' });
  const [menuVisible, setMenuVisible] = useState(true);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [iconUrl, setIconUrl] = useState('');
  const [customIconUrl, setCustomIconUrl] = useState('');
  const [editingItemId, setEditingItemId] = useState(null);
  const [isCustomIcon, setIsCustomIcon] = useState(true);
  const [iconsApplied, setIconsApplied] = useState(false);
  const [mobileToggleIconUrl, setMobileToggleIconUrl] = useState('');
  const [showMobileToggleAssetPicker, setShowMobileToggleAssetPicker] = useState(false);

  const findNavbarMenuElement = () => {
    // Try to find menu element in multiple ways
    let navbarMenuElement = elements.find(el => 
      el.parentId === element.id && 
      el.attributes?.['data-element'] === 'navbar-menu'
    );

    // If not found by parent relationship, try finding by data attribute only
    if (!navbarMenuElement) {
      navbarMenuElement = elements.find(el => 
        el.attributes?.['data-element'] === 'navbar-menu'
      );
    }

    // If still not found, try finding through container
    if (!navbarMenuElement) {
      const containerElement = elements.find(el => 
        el.parentId === element.id && 
        el.attributes?.['data-element'] === 'navbar-container'
      );
      if (containerElement) {
        navbarMenuElement = elements.find(el => 
          el.parentId === containerElement.id && 
          el.attributes?.['data-element'] === 'navbar-menu'
        );
      }
    }

    return navbarMenuElement;
  };

  useEffect(() => {    
    // Extract menu items from the navbar element's children
    if (element && elements) {
      const navbarMenuElement = findNavbarMenuElement();
      
      // Find mobile toggle icon and set its URL
      const mobileToggleButton = elements.find(el => 
        el.attributes?.['data-element'] === 'navbar-mobile-toggle'
      );
      
      if (mobileToggleButton) {
        const mobileToggleIcon = elements.find(el => 
          el.parentId === mobileToggleButton.id && 
          el.attributes?.['data-element'] === 'navbar-mobile-toggle-icon'
        );
        
        if (mobileToggleIcon && mobileToggleIcon.styles.backgroundImage) {
          const iconUrl = mobileToggleIcon.styles.backgroundImage.replace(/^url\(['"](.+)['"]\)$/, '$1');
          setMobileToggleIconUrl(iconUrl);
        }
      }

      if (navbarMenuElement) {
        // Initialize menuVisible state based on class name
        if (navbarMenuElement.className && navbarMenuElement.className.includes('navbar-mobilemenu-show')) {
          setMenuVisible(true);
        } else {
          setMenuVisible(false);
        }

        const items = navbarMenuElement.children?.map(childId => {
          const menuItem = elements.find(el => el.id === childId);
          if (!menuItem) return null;

          const link = elements.find(el => 
            el.parentId === menuItem.id && 
            el.attributes?.['data-element'] === 'navbar-link'
          );

          const submenu = elements.find(el => 
            el.parentId === menuItem.id && 
            el.attributes?.['data-element'] === 'navbar-submenu'
          );

          // Find icon image element if it exists
          const iconImage = elements.find(el => 
            el.parentId === menuItem.id && 
            el.attributes?.['data-element'] === 'navbar-icon'
          );

          const submenuItems = submenu ? submenu.children?.map(subChildId => {
            const subItem = elements.find(el => el.id === subChildId);
            const subLink = elements.find(el => 
              el.parentId === subItem?.id && 
              el.attributes?.['data-element'] === 'navbar-submenu-link'
            );
            return subLink ? {
              id: subItem.id,
              name: subLink.text,
              link: subLink.attributes?.href || '#'
            } : null;
          }).filter(Boolean) : [];

          return {
            id: menuItem.id,
            name: link?.text || '',
            link: link?.attributes?.href || '#',
            hasSubmenu: !!submenu,
            submenuItems,
            iconUrl: iconImage?.attributes?.src || ''
          };
        }).filter(Boolean) || [];

        setMenuItems(items);
      }
    }
  }, [element, elements]);

  const getExistingClassName = (dataElement) => {
    // Find an existing element with the same data-element attribute
    const existingElement = elements.find(el => 
      el.attributes?.['data-element'] === dataElement
    );
    return existingElement?.className || '';
  };

  const handleAddMenuItem = () => {
    if (!newItemName.trim()) {
      console.log('Item name is empty, returning');
      return;
    }

    const navbarMenuElement = findNavbarMenuElement();

    if (!navbarMenuElement) {
      console.log('No navbar menu element found. Element structure:', {
        selectedElement: element,
        allElements: elements.map(el => ({
          id: el.id,
          type: el.type,
          attributes: el.attributes,
          parentId: el.parentId
        }))
      });
      return;
    }

    // Get existing classNames
    const itemClassName = getExistingClassName('navbar-item');
    const linkClassName = getExistingClassName('navbar-link');
    const iconClassName = "navbar-icon";

    // Create new menu item elements
    const newItemId = Date.now();
    const newLinkId = newItemId + 1;
    const newIconId = newItemId + 2;
    
    const children = [newLinkId];
    
    // Check if any existing menu item has an icon and get its URL
    const existingMenuItems = elements.filter(el => 
      el.attributes?.['data-element'] === 'navbar-item'
    );
    const existingIcon = existingMenuItems.length > 0 ? 
      elements.find(el => 
        el.attributes?.['data-element'] === 'navbar-icon' &&
        existingMenuItems.some(item => item.children.includes(el.id))
      ) : null;

    // Determine the icon URL to use
    let currentIconUrl = '';
    if (existingIcon && existingIcon.styles.backgroundImage) {
      currentIconUrl = existingIcon.styles.backgroundImage.replace(/^url\(['"](.+)['"]\)$/, '$1');
    } else if (iconsApplied && iconUrl) {
      currentIconUrl = iconUrl;
    }
    
    // Add icon if there's a URL to use
    if (currentIconUrl) {
      children.push(newIconId);
    }

    const newMenuItem = {
      id: newItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: `${newItemName} Menu Item`,
      hierarchyName: `${newItemName} Menu Item`,
      parentId: navbarMenuElement.id,
      className: itemClassName,
      styles: {},
      children: children,
      text: '',
      attributes: {
        'data-element': 'navbar-item',
        'data-has-submenu': 'false'
      }
    };

    const newLink = {
      id: newLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: `${newItemName} Link`,
      hierarchyName: `${newItemName} Link`,
      parentId: newItemId,
      className: linkClassName,
      styles: {},
      children: [],
      text: newItemName,
      attributes: {
        'data-element': 'navbar-link',
        'href': newItemLink || '#'
      }
    };

    // Create new elements array
    let updatedElements = [...elements, newMenuItem, newLink];
    
    // Add icon image if URL is provided
    if (currentIconUrl) {
      const newIcon = {
        id: newIconId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `${newItemName} Icon`,
        hierarchyName: `${newItemName} Icon`,
        parentId: newItemId,
        className: iconClassName,
        styles: {
          width: '20px',
          height: '20px',
          marginRight: '5px',
          display: 'inline-block',
          backgroundImage: `url(${currentIconUrl})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        },
        children: [],
        text: '',
        attributes: {
          'data-element': 'navbar-icon',
          'aria-hidden': 'true'
        }
      };
      
      updatedElements = [...updatedElements, newIcon];
    }

    // Update navbar menu children
    navbarMenuElement.children = [...(navbarMenuElement.children || []), newItemId];
    
    setElements(updatedElements);
    updateElementText(navbarMenuElement.id, navbarMenuElement);

    // Reset input fields
    setNewItemName('');
    setNewItemLink('');
  };

  const handleRemoveMenuItem = (itemId) => {
    // Find all elements to remove (menu item, link, submenu if exists)
    const elementsToRemove = new Set();
    const menuItem = elements.find(el => el.id === itemId);
    
    if (menuItem) {
      elementsToRemove.add(itemId);
      menuItem.children.forEach(childId => {
        const child = elements.find(el => el.id === childId);
        if (child) {
          elementsToRemove.add(childId);
          // If child is submenu, remove its children too
          if (child.attributes?.['data-element'] === 'navbar-submenu') {
            child.children.forEach(subChildId => elementsToRemove.add(subChildId));
          }
        }
      });
    }

    // Update navbar menu children
    const navbarMenuElement = findNavbarMenuElement();

    if (navbarMenuElement) {
      navbarMenuElement.children = navbarMenuElement.children.filter(id => !elementsToRemove.has(id));
      updateElementText(navbarMenuElement.id, navbarMenuElement);
    }

    // Remove elements
    const updatedElements = elements.filter(el => !elementsToRemove.has(el.id));
    setElements(updatedElements);
  };

  const handleMoveMenuItem = (itemId, direction) => {
    console.log('Moving item:', { itemId, direction });
    
    const navbarMenuElement = findNavbarMenuElement();

    if (!navbarMenuElement) {
      console.log('No navbar menu element found. Element structure:', {
        selectedElement: element,
        allElements: elements.map(el => ({
          id: el.id,
          type: el.type,
          className: el.className,
          parentId: el.parentId
        }))
      });
      return;
    }

    const currentIndex = navbarMenuElement.children.indexOf(itemId);
    if (currentIndex === -1) {
      console.log('Item not found in menu children');
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= navbarMenuElement.children.length) {
      console.log('Invalid new index:', newIndex);
      return;
    }

    // Create a new array of children with swapped positions
    const updatedChildren = [...navbarMenuElement.children];
    [updatedChildren[currentIndex], updatedChildren[newIndex]] = 
    [updatedChildren[newIndex], updatedChildren[currentIndex]];

    // Create a new menu element with updated children
    const updatedMenuElement = {
      ...navbarMenuElement,
      children: updatedChildren
    };

    // Update the elements array with the new menu element
    const updatedElements = elements.map(el => 
      el.id === navbarMenuElement.id ? updatedMenuElement : el
    );

    console.log('Updated elements:', {
      oldChildren: navbarMenuElement.children,
      newChildren: updatedChildren
    });

    // Update both the elements array and the menu element
    setElements(updatedElements);
    updateElementText(navbarMenuElement.id, updatedMenuElement);

    // Force a re-render of menu items
    const updatedItems = updatedChildren.map(childId => {
      const menuItem = elements.find(el => el.id === childId);
      if (!menuItem) return null;

      const link = elements.find(el => 
        el.parentId === menuItem.id && 
        el.attributes?.['data-element'] === 'navbar-link'
      );

      const submenu = elements.find(el => 
        el.parentId === menuItem.id && 
        el.attributes?.['data-element'] === 'navbar-submenu'
      );

      const submenuItems = submenu ? submenu.children?.map(subChildId => {
        const subItem = elements.find(el => el.id === subChildId);
        const subLink = elements.find(el => 
          el.parentId === subItem?.id && 
          el.attributes?.['data-element'] === 'navbar-submenu-link'
        );
        return subLink ? {
          id: subItem.id,
          name: subLink.text,
          link: subLink.attributes?.href || '#'
        } : null;
      }).filter(Boolean) : [];

      return {
        id: menuItem.id,
        name: link?.text || '',
        link: link?.attributes?.href || '#',
        hasSubmenu: !!submenu,
        submenuItems
      };
    }).filter(Boolean);

    setMenuItems(updatedItems);
  };

  const handleAddSubmenuItem = (parentItemId) => {
    if (!newSubmenuItem.name.trim()) return;

    const parentItem = elements.find(el => el.id === parentItemId);
    if (!parentItem) return;

    // Get existing classNames
    const submenuClassName = getExistingClassName('navbar-submenu');
    const submenuItemClassName = getExistingClassName('navbar-submenu-item');
    const submenuLinkClassName = getExistingClassName('navbar-submenu-link');
    const linkArrowClassName = getExistingClassName('navbar-link-arrow');
    const linkClassName = getExistingClassName('navbar-link');

    // Create IDs for new elements
    const submenuId = parentItem.children.find(childId => 
      elements.find(el => el.id === childId && el.attributes?.['data-element'] === 'navbar-submenu')
    );
    const newItemId = Date.now();
    const newLinkId = newItemId + 1;

    let updatedElements = [...elements];
    let newElements = [];

    if (!submenuId) {
      // Create new submenu if it doesn't exist
      const newSubmenuId = Date.now() + 2;
      const arrowIconId = Date.now() + 3;
      
      // Find the existing link
      const existingLinkId = parentItem.children.find(childId => 
        elements.find(el => el.id === childId && el.attributes?.['data-element'] === 'navbar-link')
      );
      const existingLink = elements.find(el => el.id === existingLinkId);
      
      if (existingLink) {
        // Create arrow icon element
        const arrowIcon = {
          id: arrowIconId,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'span',
          name: `Arrow Icon`,
          hierarchyName: `Arrow Icon`,
          parentId: existingLinkId,
          className: linkArrowClassName,
          styles: {
            display: 'inline-block',
            width: '10px',
            height: '10px',
            marginLeft: '4px',
            backgroundImage: 'url("https://cdn-icons-png.flaticon.com/512/25/25623.png")',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            transition: 'transform 0.3s ease'
          },
          text: '',
          attributes: {
            'data-element': 'navbar-link-arrow',
            'aria-hidden': 'true'
          }
        };
        
        // Update existing link to include arrow icon as child
        const updatedLink = {
          ...existingLink,
          children: [...(existingLink.children || []), arrowIconId]
        };
        
        updatedElements = updatedElements.map(el => 
          el.id === existingLinkId ? updatedLink : el
        );
        
        newElements.push(arrowIcon);
      }
      
      const newSubmenu = {
        id: newSubmenuId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'ul',
        name: 'Submenu',
        hierarchyName: 'Submenu',
        parentId: parentItemId,
        className: submenuClassName,
        styles: {},
        children: [newItemId],
        attributes: {
          'data-element': 'navbar-submenu'
        }
      };

      // Update parent item
      const updatedParentItem = {
        ...parentItem,
        children: [...parentItem.children, newSubmenuId],
        attributes: {
          ...parentItem.attributes,
          'data-has-submenu': 'true'
        },
        interactions: {
            events: [
              {
                id: Date.now(),
                type: 'hover',
                action: 'toggleClass',
                direction: 'in',
                label: 'Show submenu on hover',
                value: {
                  targetId: newSubmenuId,
                  className: 'active-submenu'
                }
              },
              {
                id: Date.now() + 1,
                type: 'hover',
                action: 'toggleClass',
                direction: 'out',
                label: 'Hide submenu on hover out',
                value: {
                  targetId: newSubmenuId,
                  className: 'active-submenu',
                  remove: true
                }
              },
              {
                id: Date.now() + 2,
                type: 'hover',
                action: 'toggleClass',
                direction: 'in',
                label: 'Rotate arrow icon on hover',
                value: {
                  targetId: arrowIconId,
                  className: 'navbar-link-arrow-up'
                }
              },
              {
                id: Date.now() + 3,
                type: 'hover',
                action: 'toggleClass',
                direction: 'out',
                label: 'Restore arrow icon on hover out',
                value: {
                  targetId: arrowIconId,
                  className: 'navbar-link-arrow-up',
                  remove: true
                }
              }
            ]
          }
      };

      newElements.push(newSubmenu);
      updatedElements = updatedElements.map(el => 
        el.id === parentItemId ? updatedParentItem : el
      );
    } else {
      // Add to existing submenu
      const submenu = elements.find(el => el.id === submenuId);
      const updatedSubmenu = {
        ...submenu,
        children: [...(submenu.children || []), newItemId]
      };
      updatedElements = updatedElements.map(el => 
        el.id === submenuId ? updatedSubmenu : el
      );
    }

    // Create submenu item and link
    const newSubmenuItemElement = {
      id: newItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: `${newSubmenuItem.name} Submenu Item`,
      hierarchyName: `${newSubmenuItem.name} Submenu Item`,
      parentId: submenuId || (Date.now() + 2),
      className: submenuItemClassName,
      styles: {},
      children: [newLinkId],
      attributes: {
        'data-element': 'navbar-submenu-item'
      }
    };

    const newSubmenuLinkElement = {
      id: newLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: `${newSubmenuItem.name} Link`,
      hierarchyName: `${newSubmenuItem.name} Link`,
      parentId: newItemId,
      className: submenuLinkClassName,
      styles: {},
      children: [],
      text: newSubmenuItem.name,
      attributes: {
        'data-element': 'navbar-submenu-link',
        'href': newSubmenuItem.link || '#'
      }
    };

    // Update elements array
    setElements([...updatedElements, ...newElements, newSubmenuItemElement, newSubmenuLinkElement]);
    
    // Reset input
    setNewSubmenuItem({ name: '', link: '' });
  };

  const handleRemoveSubmenuItem = (parentItemId, submenuItemId) => {
    // Find the parent menu item
    const parentItem = elements.find(el => el.id === parentItemId);
    if (!parentItem) return;

    // Find the submenu container
    const submenuElement = elements.find(el => 
      el.parentId === parentItemId && 
      el.attributes?.['data-element'] === 'navbar-submenu'
    );
    
    if (!submenuElement) return;

    // Find the submenu item element
    const submenuItem = elements.find(el => el.id === submenuItemId);
    if (!submenuItem) return;

    // Create a set of elements to remove (submenu item and its children)
    const elementsToRemove = new Set();
    elementsToRemove.add(submenuItemId);
    
    // Add children (link)
    submenuItem.children.forEach(childId => elementsToRemove.add(childId));

    // Update the submenu's children array
    const updatedSubmenuElement = {
      ...submenuElement,
      children: submenuElement.children.filter(id => !elementsToRemove.has(id))
    };

    // If the submenu has no more children, remove the submenu and update parent
    let updatedParentItem = parentItem;
    let shouldRemoveSubmenu = false;
    let updatedElements = [...elements];
    
    if (updatedSubmenuElement.children.length === 0) {
      elementsToRemove.add(submenuElement.id);
      shouldRemoveSubmenu = true;
      
      // Find the navbar link to remove the arrow icon
      const linkId = parentItem.children.find(childId => 
        elements.find(el => el.id === childId && el.attributes?.['data-element'] === 'navbar-link')
      );
      
      if (linkId) {
        const linkElement = elements.find(el => el.id === linkId);
        
        // Find and remove arrow icon
        if (linkElement && linkElement.children && linkElement.children.length > 0) {
          const arrowIconId = linkElement.children.find(childId => 
            elements.find(el => el.id === childId && el.attributes?.['data-element'] === 'navbar-link-arrow')
          );
          
          if (arrowIconId) {
            elementsToRemove.add(arrowIconId);
            
            // Update link element to remove arrow icon reference
            const updatedLinkElement = {
              ...linkElement,
              children: linkElement.children.filter(id => id !== arrowIconId)
            };
            
            updatedElements = updatedElements.map(el => 
              el.id === linkId ? updatedLinkElement : el
            );
          }
        }
      }
      
      // Update parent item to remove submenu reference and data-has-submenu attribute
      updatedParentItem = {
        ...parentItem,
        children: parentItem.children.filter(id => id !== submenuElement.id),
        attributes: {
          ...parentItem.attributes,
          'data-has-submenu': 'false'
        },
        interactions: {} // Remove submenu hover interactions
      };
    }

    // Update the elements array
    updatedElements = updatedElements
      .filter(el => !elementsToRemove.has(el.id))
      .map(el => {
        if (el.id === submenuElement.id && !shouldRemoveSubmenu) {
          return updatedSubmenuElement;
        }
        if (el.id === parentItemId && shouldRemoveSubmenu) {
          return updatedParentItem;
        }
        return el;
      });
    
    setElements(updatedElements);

    // Update menuItems state to reflect changes immediately
    setMenuItems(prevMenuItems => {
      return prevMenuItems.map(item => {
        if (item.id === parentItemId) {
          // If we've removed the submenu entirely
          if (shouldRemoveSubmenu) {
            return {
              ...item,
              hasSubmenu: false,
              submenuItems: []
            };
          }
          
          // Just update the submenu items
          return {
            ...item,
            submenuItems: item.submenuItems.filter(subItem => subItem.id !== submenuItemId)
          };
        }
        return item;
      });
    });
  };

  const handleMoveSubmenuItem = (parentItemId, submenuItemId, direction) => {
    // Find the parent menu item
    const parentItem = elements.find(el => el.id === parentItemId);
    if (!parentItem) return;

    // Find the submenu container
    const submenuElement = elements.find(el => 
      el.parentId === parentItemId && 
      el.attributes?.['data-element'] === 'navbar-submenu'
    );
    
    if (!submenuElement) return;

    // Find the current index of the submenu item
    const currentIndex = submenuElement.children.indexOf(submenuItemId);
    if (currentIndex === -1) return;

    // Calculate the new index
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= submenuElement.children.length) return;

    // Create a new array of children with swapped positions
    const updatedChildren = [...submenuElement.children];
    [updatedChildren[currentIndex], updatedChildren[newIndex]] = 
    [updatedChildren[newIndex], updatedChildren[currentIndex]];

    // Update submenu element
    const updatedSubmenuElement = {
      ...submenuElement,
      children: updatedChildren
    };

    // Update elements array
    const updatedElements = elements.map(el => 
      el.id === submenuElement.id ? updatedSubmenuElement : el
    );

    setElements(updatedElements);

    // Update menuItems state to reflect changes
    setMenuItems(prevMenuItems => {
      return prevMenuItems.map(item => {
        if (item.id === parentItemId && item.submenuItems) {
          // Get the updated order of submenu items
          const updatedSubmenuItems = [...item.submenuItems];
          const subItemIndex = updatedSubmenuItems.findIndex(subItem => subItem.id === submenuItemId);
          
          if (subItemIndex !== -1) {
            const newSubItemIndex = direction === 'up' ? subItemIndex - 1 : subItemIndex + 1;
            
            if (newSubItemIndex >= 0 && newSubItemIndex < updatedSubmenuItems.length) {
              // Swap the items
              [updatedSubmenuItems[subItemIndex], updatedSubmenuItems[newSubItemIndex]] = 
              [updatedSubmenuItems[newSubItemIndex], updatedSubmenuItems[subItemIndex]];
              
              // Return the updated item
              return {
                ...item,
                submenuItems: updatedSubmenuItems
              };
            }
          }
        }
        return item;
      });
    });
  };

  const toggleNavbarMenuVisibility = () => {
    const navbarMenuElement = findNavbarMenuElement();
    
    if (!navbarMenuElement) return;
    
    // Toggle visibility by adding/removing the show class
    const updatedElements = elements.map(el => {
      if (el.id === navbarMenuElement.id) {
        // Get current class name
        let className = el.className || '';
        
        if (menuVisible) {
          // Remove the navbar-mobilemenu-show class if it exists
          className = className.replace('navbar-mobilemenu-show', '').trim();
        } else {
          // Add the navbar-mobilemenu-show class if it's not already there
          if (!className.includes('navbar-mobilemenu-show')) {
            className = className ? `${className} navbar-mobilemenu-show` : 'navbar-mobilemenu-show';
          }
        }
        
        return {
          ...el,
          className
        };
      }
      return el;
    });
    
    setElements(updatedElements);
    setMenuVisible(!menuVisible);
  };

  const handleOpenAssetPicker = () => {
    setShowAssetPicker(true);
  };

  const handleCloseAssetPicker = () => {
    setShowAssetPicker(false);
  };

  const handleUpdateIcon = () => {
    if (!iconUrl) return;
    
    // Find all menu items
    const menuItems = elements.filter(el => 
      el.attributes?.['data-element'] === 'navbar-item'
    );
    
    let updatedElements = [...elements];
    
    // Update all menu items with the new icon
    menuItems.forEach(menuItem => {
      // Find existing icon for this menu item
      const existingIconId = menuItem.children.find(childId => 
        elements.find(el => el.id === childId && el.attributes?.['data-element'] === 'navbar-icon')
      );
      
      if (existingIconId) {
        // Update existing icon
        updatedElements = updatedElements.map(el => {
          if (el.id === existingIconId) {
            return {
              ...el,
              styles: {
                ...el.styles,
                backgroundImage: `url(${iconUrl})`
              }
            };
          }
          return el;
        });
      }
    });
    
    // Update the classStylesRegistry for the navbar-link-arrow class
    setClassStylesRegistry(prev => {
      const newRegistry = { ...prev };
      
      // Update styles for all devices
      Object.keys(newRegistry).forEach(device => {
        const deviceStyles = newRegistry[device];
        const arrowStyles = deviceStyles.get('navbar-link-arrow') || {};
        
        deviceStyles.set('navbar-link-arrow', {
          ...arrowStyles,
          width: '20px',
          height: '20px',
          marginRight: '5px',
          display: 'inline-block',
          backgroundImage: `url(${iconUrl})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          transition: 'transform 0.3s ease'
        });
      });
      
      return newRegistry;
    });
    
    setElements(updatedElements);
    setIconsApplied(true);
    
    // Update menuItems state to reflect changes
    setMenuItems(prevMenuItems => 
      prevMenuItems.map(item => ({
        ...item,
        iconUrl: iconUrl
      }))
    );
  };

  const handleSelectAsset = (asset) => {
    setIconUrl(asset.url);
    setIsCustomIcon(false);
    setShowAssetPicker(false);
    
    if (iconsApplied) {
      // If icons were already applied, automatically update them
      const finalIconUrl = asset.url;
      if (!finalIconUrl) return;
      
      // Find all menu items
      const menuItems = elements.filter(el => 
        el.attributes?.['data-element'] === 'navbar-item'
      );
      
      let updatedElements = [...elements];
      
      // Update all menu items with the new icon
      menuItems.forEach(menuItem => {
        // Find existing icon for this menu item
        const existingIconId = menuItem.children.find(childId => 
          elements.find(el => el.id === childId && el.attributes?.['data-element'] === 'navbar-icon')
        );
        
        if (existingIconId) {
          // Update existing icon
          updatedElements = updatedElements.map(el => {
            if (el.id === existingIconId) {
              return {
                ...el,
                styles: {
                  ...el.styles,
                  backgroundImage: `url(${finalIconUrl})`
                }
              };
            }
            return el;
          });
        }
      });
      
      // Update the classStylesRegistry for the navbar-link-arrow class
      setClassStylesRegistry(prev => {
        const newRegistry = { ...prev };
        
        // Update styles for all devices
        Object.keys(newRegistry).forEach(device => {
          const deviceStyles = newRegistry[device];
          const arrowStyles = deviceStyles.get('navbar-link-arrow') || {};
          
          deviceStyles.set('navbar-link-arrow', {
            ...arrowStyles,
            backgroundImage: `url(${finalIconUrl})`
          });
        });
        
        return newRegistry;
      });
      
      setElements(updatedElements);
      
      // Update menuItems state to reflect changes
      setMenuItems(prevMenuItems => 
        prevMenuItems.map(item => ({
          ...item,
          iconUrl: finalIconUrl
        }))
      );
    }
  };

  const handleRemoveIcon = () => {
    // Find all menu items
    const menuItems = elements.filter(el => 
      el.attributes?.['data-element'] === 'navbar-item'
    );
    
    let updatedElements = [...elements];
    
    // Remove icons from all menu items
    menuItems.forEach(menuItem => {
      // Find icon element
      const iconId = menuItem.children.find(childId => 
        elements.find(el => el.id === childId && el.attributes?.['data-element'] === 'navbar-icon')
      );
      
      if (iconId) {
        // Update parent's children array to remove icon reference
        const updatedMenuItem = {
          ...menuItem,
          children: menuItem.children.filter(id => id !== iconId)
        };
        
        // Remove icon element and update menu item
        updatedElements = updatedElements
          .filter(el => el.id !== iconId)
          .map(el => el.id === menuItem.id ? updatedMenuItem : el);
      }
    });
    
    // Update the classStylesRegistry for the navbar-link-arrow class
    setClassStylesRegistry(prev => {
      const newRegistry = { ...prev };
      
      // Update styles for all devices
      Object.keys(newRegistry).forEach(device => {
        const deviceStyles = newRegistry[device];
        const arrowStyles = deviceStyles.get('navbar-link-arrow') || {};
        
        deviceStyles.set('navbar-link-arrow', {
          ...arrowStyles,
          backgroundImage: 'none'
        });
      });
      
      return newRegistry;
    });
    
    setElements(updatedElements);
    setIconsApplied(false);
    
    // Update menu items state
    setMenuItems(prevMenuItems => 
      prevMenuItems.map(item => ({
        ...item,
        iconUrl: ''
      }))
    );
  };

  const handleSelectMobileToggleAsset = (asset) => {
    // Set the URL directly from the asset, similar to dropdown icon handling
    setMobileToggleIconUrl(asset.url);
    setShowMobileToggleAssetPicker(false);
    
    // Create a new elements array with the updated icon
    const updatedElements = [...elements];
    
    // Find the mobile toggle button and its icon
    const mobileToggleButton = elements.find(el => 
      el.attributes?.['data-element'] === 'navbar-mobile-toggle'
    );
    
    if (mobileToggleButton) {
      const mobileToggleIcon = elements.find(el => 
        el.parentId === mobileToggleButton.id && 
        el.attributes?.['data-element'] === 'navbar-mobile-toggle-icon'
      );
      
      if (mobileToggleIcon) {
        // Update the existing icon's styles
        const updatedIcon = {
          ...mobileToggleIcon,
          styles: {
            ...mobileToggleIcon.styles,
            width: '24px',
            height: '24px',
            display: 'inline-block',
            backgroundImage: `url(${asset.url})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }
        };
        
        // Update the elements array
        const elementIndex = updatedElements.findIndex(el => el.id === mobileToggleIcon.id);
        if (elementIndex !== -1) {
          updatedElements[elementIndex] = updatedIcon;
        }
      }
    }
    
    // Update the elements state
    setElements(updatedElements);
    
    // Update the classStylesRegistry
    setClassStylesRegistry(prev => {
      const newRegistry = { ...prev };
      
      Object.keys(newRegistry).forEach(device => {
        const deviceStyles = newRegistry[device];
        
        deviceStyles.set('navbar-mobile-toggle-icon', {
          width: '24px',
          height: '24px',
          display: 'inline-block',
          backgroundImage: `url(${asset.url})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        });
      });
      
      return newRegistry;
    });
  };

  const handleUpdateMobileToggleIcon = () => {
    if (!mobileToggleIconUrl) return;

    const updatedElements = [...elements];
    
    // Find the mobile toggle button and its icon
    const mobileToggleButton = elements.find(el => 
      el.attributes?.['data-element'] === 'navbar-mobile-toggle'
    );
    
    if (mobileToggleButton) {
      const mobileToggleIcon = elements.find(el => 
        el.parentId === mobileToggleButton.id && 
        el.attributes?.['data-element'] === 'navbar-mobile-toggle-icon'
      );
      
      if (mobileToggleIcon) {
        // Update the existing icon's styles
        const updatedIcon = {
          ...mobileToggleIcon,
          styles: {
            ...mobileToggleIcon.styles,
            width: '24px',
            height: '24px',
            display: 'inline-block',
            backgroundImage: `url(${mobileToggleIconUrl})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }
        };
        
        // Update the elements array
        const elementIndex = updatedElements.findIndex(el => el.id === mobileToggleIcon.id);
        if (elementIndex !== -1) {
          updatedElements[elementIndex] = updatedIcon;
        }
      }
    }
    
    // Update the elements state
    setElements(updatedElements);
    
    // Update the classStylesRegistry
    setClassStylesRegistry(prev => {
      const newRegistry = { ...prev };
      
      Object.keys(newRegistry).forEach(device => {
        const deviceStyles = newRegistry[device];
        
        deviceStyles.set('navbar-mobile-toggle-icon', {
          width: '24px',
          height: '24px',
          display: 'inline-block',
          backgroundImage: `url(${mobileToggleIconUrl})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        });
      });
      
      return newRegistry;
    });
  };

  return (
    <div className="navbar-settings">
      <h4>Navigation Menu Items</h4>
      
      {/* Global Icon Settings */}
      <div className="global-icon-settings">
        <h5>Dropdown Icon Settings</h5>
        <div className="asset-preview-container">
          {iconUrl ? (
            <div className="selected-asset">
              <span 
                className="selected-icon"
                style={{
                  display: 'block',
                  width: '30px',
                  height: '30px',
                  backgroundImage: `url(${iconUrl})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
              <div className="icon-actions">
                {iconsApplied ? (
                  <button 
                    className="icon-change-btn"
                    onClick={handleOpenAssetPicker}
                  >
                    <FaImage /> Change Icon
                  </button>
                ) : (
                  <div className="icon-edit-actions">
                    <button 
                      className="icon-save-btn"
                      onClick={handleUpdateIcon}
                    >
                      Apply Icon
                    </button>
                    {/* <button 
                      className="icon-cancel-btn"
                      onClick={() => {
                        setIconUrl('');
                        setCustomIconUrl('');
                        setIsCustomIcon(true);
                      }}
                    >
                      Cancel
                    </button> */}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <button 
              className="select-asset-btn"
              onClick={handleOpenAssetPicker}
            >
              <FaImage /> Select Dropdown Icon
            </button>
          )}
        </div>
      </div>

      {/* Mobile Toggle Icon Settings */}
      <div className="global-icon-settings">
        <h5>Mobile Menu Toggle Icon</h5>
        <div className="asset-preview-container">
          {mobileToggleIconUrl ? (
            <div className="selected-asset">
              <span 
                className="selected-icon"
                style={{
                  display: 'block',
                  width: '30px',
                  height: '30px',
                  backgroundImage: `url(${mobileToggleIconUrl})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
              <div className="icon-actions">
                <button 
                  className="icon-change-btn"
                  onClick={() => setShowMobileToggleAssetPicker(true)}
                >
                  <FaImage /> Change Icon
                </button>
              </div>
            </div>
          ) : (
            <button 
              className="select-asset-btn"
              onClick={() => setShowMobileToggleAssetPicker(true)}
            >
              <FaImage /> Select Toggle Icon
            </button>
          )}
        </div>
      </div>

      {/* Menu Visibility Toggle */}
      <div className="menu-visibility-toggle">
        <button 
          className={`visibility-toggle-btn ${menuVisible ? 'visible' : 'hidden'}`}
          onClick={toggleNavbarMenuVisibility}
        >
          {menuVisible ? (
            <>
              <FaEye /> Mobile Menu Visible
            </>
          ) : (
            <>
              <FaEyeSlash /> Mobile Menu Hidden
            </>
          )}
        </button>
      </div>

      {/* Existing Menu Items */}
      <div className="menu-items-list">
        {menuItems.map((item, index) => (
          <div key={item.id} className="menu-item">
            <div className="menu-item-main">
              <div className="menu-item-content">
                <div className="menu-item-header">
                  <span className="menu-item-name">
                    {item.iconUrl && (
                      <span 
                        className="menu-item-icon-preview"
                        style={{
                          backgroundImage: `url(${item.iconUrl})`,
                          backgroundSize: 'contain',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat'
                        }}
                      />
                    )}
                    {item.name}
                    {item.hasSubmenu && 
                      <span className="menu-item-indicator">
                        <FaCaretDown size={14} />
                      </span>
                    }
                  </span>
                  <span className="menu-item-link">{item.link}</span>
                </div>
              </div>
              
              <div className="menu-item-actions">
                <button
                  onClick={() => handleMoveMenuItem(item.id, 'up')}
                  disabled={index === 0}
                  className="action-btn"
                  title="Move Up"
                >
                  <FaArrowUp />
                </button>
                <button
                  onClick={() => handleMoveMenuItem(item.id, 'down')}
                  disabled={index === menuItems.length - 1}
                  className="action-btn"
                  title="Move Down"
                >
                  <FaArrowDown />
                </button>
                <button
                  onClick={() => handleRemoveMenuItem(item.id)}
                  className="action-btn delete"
                  title="Remove Item"
                >
                  <FaTrash />
                </button>
              </div>
            </div>

            {/* Icon edit panel */}
            {editingItemId === item.id && (
              <div className="icon-edit-panel">
                <div className="icon-source-toggle">
                  <label>
                    <input
                      type="radio"
                      name="iconSource"
                      checked={isCustomIcon}
                      onChange={() => setIsCustomIcon(true)}
                    />
                    Custom URL
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="iconSource"
                      checked={!isCustomIcon}
                      onChange={() => setIsCustomIcon(false)}
                    />
                    Media Library
                  </label>
                </div>
                
                {isCustomIcon ? (
                  <input
                    type="text"
                    value={customIconUrl}
                    onChange={(e) => setCustomIconUrl(e.target.value)}
                    placeholder="Enter icon URL"
                    className="setting-input"
                  />
                ) : (
                  <div className="asset-preview-container">
                    {iconUrl ? (
                      <div className="selected-asset">
                        <span 
                          className="selected-icon"
                          style={{
                            display: 'block',
                            width: '20px',
                            height: '20px',
                            backgroundImage: `url(${iconUrl})`,
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                          }}
                        />
                      </div>
                    ) : (
                      <button 
                        className="select-asset-btn"
                        onClick={handleOpenAssetPicker}
                      >
                        <FaImage /> Select Icon
                      </button>
                    )}
                  </div>
                )}
                
                <div className="icon-edit-actions">
                  <button 
                    className="icon-save-btn"
                    onClick={handleUpdateIcon}
                    disabled={isCustomIcon ? !customIconUrl : !iconUrl}
                  >
                    Save Icon
                  </button>
                  <button 
                    className="icon-cancel-btn"
                    onClick={() => {
                      setEditingItemId(null);
                      setIconUrl('');
                      setCustomIconUrl('');
                      setIsCustomIcon(true);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="submenu-row">
              <button
                onClick={() => {
                  setShowSubmenuFor(showSubmenuFor === item.id ? null : item.id);
                  
                  // Find all submenu elements and the current submenu
                  const allSubmenuElements = elements.filter(el => 
                    el.attributes?.['data-element'] === 'navbar-submenu'
                  );
                  const currentSubmenuElement = elements.find(el => 
                    el.parentId === item.id && 
                    el.attributes?.['data-element'] === 'navbar-submenu'
                  );

                  if (currentSubmenuElement) {
                    // Update elements array - remove active-submenu from all submenus first
                    const updatedElements = elements.map(el => {
                      if (allSubmenuElements.some(submenu => submenu.id === el.id)) {
                        return {
                          ...el,
                          className: el.className.replace(' active-submenu', '')
                        };
                      }
                      return el;
                    });

                    // Then add active-submenu only to the clicked submenu if it's being opened
                    const finalElements = updatedElements.map(el => {
                      if (el.id === currentSubmenuElement.id && showSubmenuFor !== item.id) {
                        return {
                          ...el,
                          className: `${el.className} active-submenu`
                        };
                      }
                      return el;
                    });

                    setElements(finalElements);
                  }
                }}
                className={`show-submenu-btn ${item.hasSubmenu ? 'has-submenu' : 'no-submenu'} ${showSubmenuFor === item.id ? 'active' : ''}`}
              >
                <FaChevronDown />
                {item.hasSubmenu 
                  ? `${item.submenuItems?.length || 0} Submenu Items` 
                  : 'Add Submenu Items'}
              </button>

              {/* Submenu Content */}
              {showSubmenuFor === item.id && (
                <div className="submenu-content">
                  {/* Existing Submenu Items */}
                  {item.hasSubmenu && item.submenuItems && item.submenuItems.length > 0 && (
                    <div className="submenu-items">
                      {item.submenuItems.map((subItem, subIndex) => (
                        <div key={subItem.id} className="submenu-item">
                          <div className="submenu-item-content">
                            <span className="submenu-item-name">{subItem.name}</span>
                            <span className="submenu-item-link">{subItem.link}</span>
                          </div>
                          <div className="submenu-item-actions">
                            <button
                              onClick={() => handleMoveSubmenuItem(item.id, subItem.id, 'up')}
                              disabled={subIndex === 0}
                              className="submenu-action-btn"
                              title="Move Up"
                            >
                              <FaArrowUp />
                            </button>
                            <button
                              onClick={() => handleMoveSubmenuItem(item.id, subItem.id, 'down')}
                              disabled={subIndex === item.submenuItems.length - 1}
                              className="submenu-action-btn"
                              title="Move Down"
                            >
                              <FaArrowDown />
                            </button>
                            <button
                              onClick={() => handleRemoveSubmenuItem(item.id, subItem.id)}
                              className="submenu-delete-btn"
                              title="Remove Submenu Item"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Submenu Item Form */}
                  <div className="add-submenu-form">
                    <input
                      type="text"
                      value={newSubmenuItem.name}
                      onChange={(e) => setNewSubmenuItem({
                        ...newSubmenuItem,
                        name: e.target.value
                      })}
                      placeholder="Submenu Item Name"
                      className="setting-input"
                    />
                    <input
                      type="text"
                      value={newSubmenuItem.link}
                      onChange={(e) => setNewSubmenuItem({
                        ...newSubmenuItem,
                        link: e.target.value
                      })}
                      placeholder="Link (optional)"
                      className="setting-input"
                    />
                    <button
                      onClick={() => handleAddSubmenuItem(item.id)}
                      disabled={!newSubmenuItem.name.trim()}
                      className="add-submenu-btn"
                    >
                      <FaPlus /> Add Submenu Item
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add New Menu Item */}
      <div className="add-menu-item">
        <h5>Add New Menu Item</h5>
        <div className="input-group">
          <input
            type="text"
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            placeholder="Menu Item Name"
            className="setting-input"
          />
          <input
            type="text"
            value={newItemLink}
            onChange={(e) => setNewItemLink(e.target.value)}
            placeholder="Link (optional)"
            className="setting-input"
          />
          
          <button
            onClick={handleAddMenuItem}
            disabled={!newItemName.trim()}
            className="add-item-btn"
          >
            <FaPlus /> Add Item
          </button>
        </div>
      </div>

      {/* Asset Picker Popups */}
      {showAssetPicker && (
        <AssetPickerPopup 
          assets={assets}
          onClose={handleCloseAssetPicker}
          onSelectAsset={handleSelectAsset}
        />
      )}
      {showMobileToggleAssetPicker && (
        <AssetPickerPopup 
          assets={assets}
          onClose={() => setShowMobileToggleAssetPicker(false)}
          onSelectAsset={handleSelectMobileToggleAsset}
        />
      )}
    </div>
  );
};

export default NavbarSettings; 